'use client'

import { ReactNode, useEffect } from 'react'
import Lenis from 'lenis'
import { frame, cancelFrame } from 'framer-motion'

/**
 * Global smooth scroll via Lenis, driven by Framer Motion's frame loop so that
 * `useScroll`-based parallax stays perfectly in sync with the smoothed scroll
 * position (no inter-section stutter). Disabled when reduced motion is preferred.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) return
    const isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches

    const lenis = new Lenis({
      lerp: 0.13,
      smoothWheel: !isTouch,
      wheelMultiplier: 1,
      touchMultiplier: 1.2,
      anchors: true,
      // We drive Lenis from Framer's loop below, so disable its internal RAF.
      autoRaf: false,
    })

    // Single shared loop: Framer reads scroll AFTER Lenis has updated it.
    const update = (data: { timestamp: number }) => {
      lenis.raf(data.timestamp)
    }
    frame.update(update, true)

    return () => {
      cancelFrame(update)
      lenis.destroy()
    }
  }, [])

  return <>{children}</>
}
